import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import useGlobalStore from '../store/useGlobalStore';
import clientVersionInfo from '../version.json';

// "" on production, "/apex-saas" on development (set via PUBLIC_URL at build time)
const BASE_PATH = process.env.PUBLIC_URL;
const THROTTLE_LIMIT = 30000; // 30 seconds
const CIRCUIT_BREAKER_LIMIT = 3;
const CIRCUIT_BREAKER_WINDOW = 900000; // 15 minutes

// Helper to unregister Service Workers
async function tryUnregisterServiceWorkers() {
    try {
        if ('serviceWorker' in navigator) {
            const registrations = await navigator.serviceWorker.getRegistrations();
            await Promise.all(registrations.map((r) => r.unregister()));
            console.log('[AutoUpdate] Service workers unregistered successfully');
        }
    } catch (e) {
        console.warn('[AutoUpdate] Failed to unregister service workers', e);
    }
}

// Helper to clear CacheStorage
async function tryClearCacheStorage() {
    try {
        if ('caches' in window) {
            const cacheNames = await caches.keys();
            await Promise.all(cacheNames.map((name) => caches.delete(name)));
            console.log('[AutoUpdate] CacheStorage cleared successfully');
        }
    } catch (e) {
        console.warn('[AutoUpdate] Failed to clear CacheStorage', e);
    }
}

// Perform hard page refresh with cache busting
async function triggerHardReload(targetVersion) {
    try {
        // 1) Circuit Breaker validation to prevent infinite loops
        const now = Date.now();
        const reloadHistoryRaw = sessionStorage.getItem('apex_reload_history');
        let reloadHistory = reloadHistoryRaw ? JSON.parse(reloadHistoryRaw) : [];

        // Filter events within window
        reloadHistory = reloadHistory.filter((t) => now - t < CIRCUIT_BREAKER_WINDOW);

        if (reloadHistory.length >= CIRCUIT_BREAKER_LIMIT) {
            console.error('[AutoUpdate] Circuit breaker active. Preventing further auto-reloads.');
            toast.error('Multiple update attempts failed. Please manually reload and clear your browser cache (Ctrl + F5).', {
                duration: Infinity,
            });
            return;
        }

        // Record reload attempt
        reloadHistory.push(now);
        sessionStorage.setItem('apex_reload_history', JSON.stringify(reloadHistory));

        // 2) Purge storage and service workers
        await tryUnregisterServiceWorkers();
        await tryClearCacheStorage();

        // 3) Update version tracker
        if (targetVersion) {
            localStorage.setItem('app_version', targetVersion);
        }

        // 4) Build cache-busted destination URL
        const currentUrl = new URL(window.location.href);
        currentUrl.searchParams.delete('_v');
        currentUrl.searchParams.delete('_cache');
        currentUrl.searchParams.set('_cache', `${now}-${Math.random().toString(36).substring(2, 9)}`);

        console.log('[AutoUpdate] Triggering hard reload to: ', currentUrl.toString());
        window.location.replace(currentUrl.toString());
    } catch (err) {
        console.error('[AutoUpdate] Replace failed, fallback to location.reload', err);
        window.location.reload();
    }
}

export default function AutoUpdateChecker() {
    const location = useLocation();
    const lastCheckedRef = useRef(0);
    const hasPromptedRef = useRef(false);
    const broadcastChannelRef = useRef(null);

    // Initialize BroadcastChannel to coordinate versions across browser tabs
    useEffect(() => {
        if ('BroadcastChannel' in window) {
            const channel = new BroadcastChannel('apex-app-update');
            broadcastChannelRef.current = channel;

            channel.onmessage = (event) => {
                if (event.data?.type === 'UPDATE_AVAILABLE') {
                    console.log('[AutoUpdate] Received update notification from another tab');
                    showUpdatePrompt(event.data.version);
                }
            };
        }

        return () => {
            if (broadcastChannelRef.current) {
                broadcastChannelRef.current.close();
            }
        };
    }, []);

    // Show the Sonner update toast notification
    const showUpdatePrompt = (newVersion) => {
        if (hasPromptedRef.current) return;
        hasPromptedRef.current = true;

        const isDirty = useGlobalStore.getState().isDirty;

        if (isDirty) {
            // Unsaved form data is present: show persistent cautionary prompt
            toast.warning('A new version of the app is available. Please save your work and reload.', {
                duration: Infinity,
                action: {
                    label: 'Update Now',
                    onClick: () => triggerHardReload(newVersion),
                },
            });
        } else {
            // Clean state: show non-intrusive success prompt
            toast.info('An application update is ready. Click update to refresh.', {
                duration: 15000,
                action: {
                    label: 'Update',
                    onClick: () => triggerHardReload(newVersion),
                },
            });
        }
    };

    // The main function to poll server for version.json metadata
    const checkVersion = async () => {
        // Skip check in local development to avoid annoying refreshes
        if (clientVersionInfo.version === 'dev') {
            return;
        }

        const now = Date.now();
        // Throttle checks to prevent spamming the server
        if (now - lastCheckedRef.current < THROTTLE_LIMIT) {
            return;
        }
        lastCheckedRef.current = now;

        try {
            const response = await fetch(`${BASE_PATH}/version.json?t=${now}`, {
                cache: 'no-store',
                headers: {
                    'Cache-Control': 'no-cache, no-store, must-revalidate',
                    Pragma: 'no-cache',
                    Expires: '0',
                },
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch version metadata: ${response.status}`);
            }

            const data = await response.json();
            const serverVersion = data.version;

            if (serverVersion && serverVersion !== clientVersionInfo.version) {
                console.log(`[AutoUpdate] Version mismatch! Client=${clientVersionInfo.version} Server=${serverVersion}`);

                // Show update notification to user
                showUpdatePrompt(serverVersion);

                // Inform other open browser tabs
                if (broadcastChannelRef.current) {
                    broadcastChannelRef.current.postMessage({
                        type: 'UPDATE_AVAILABLE',
                        version: serverVersion,
                    });
                }
            }
        } catch (error) {
            console.warn('[AutoUpdate] Version verification check failed:', error);
        }
    };

    // Trigger check on location (route) changes
    useEffect(() => {
        checkVersion();
    }, [location.pathname, location.search]);

    // Trigger check on window focus and visibility events
    useEffect(() => {
        const handleFocus = () => {
            checkVersion();
        };

        const handleVisibility = () => {
            if (document.visibilityState === 'visible') {
                checkVersion();
            }
        };

        window.addEventListener('focus', handleFocus);
        document.addEventListener('visibilitychange', handleVisibility);

        return () => {
            window.removeEventListener('focus', handleFocus);
            document.removeEventListener('visibilitychange', handleVisibility);
        };
    }, []);

    // Global listener to capture ChunkLoadErrors and dynamically trigger reload
    useEffect(() => {
        const handleError = (event) => {
            const errorMsg = event.message || '';
            const isChunkError =
                errorMsg.includes('ChunkLoadError') || errorMsg.includes('Loading chunk') || errorMsg.includes('Failed to fetch dynamically imported module');
            if (isChunkError) {
                console.warn('[AutoUpdate] Chunk load error captured. Forcing reload...');
                event.preventDefault();
                triggerHardReload();
            }
        };

        const handleRejection = (event) => {
            const reason = event.reason;
            if (reason) {
                const reasonMsg = reason.message || '';
                const isChunkRejection =
                    reason.name === 'ChunkLoadError' ||
                    /Loading chunk|Loading CSS chunk/i.test(reasonMsg) ||
                    /Failed to fetch dynamically imported module/i.test(reasonMsg);
                if (isChunkRejection) {
                    console.warn('[AutoUpdate] Unhandled chunk rejection captured. Forcing reload...');
                    event.preventDefault();
                    triggerHardReload();
                }
            }
        };

        window.addEventListener('error', handleError, true);
        window.addEventListener('unhandledrejection', handleRejection);

        return () => {
            window.removeEventListener('error', handleError, true);
            window.removeEventListener('unhandledrejection', handleRejection);
        };
    }, []);

    return null; // This component is pure logic, renders nothing in the UI layout
}
